import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  addEdge,
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  useStoreApi,
  useEdgesState,
  useNodesState,
} from '@xyflow/react';
import type { ComponentType, CSSProperties } from 'react';
import type { Connection, Edge, NodeChange } from '@xyflow/react';
import {
  AssetType,
  AspectRatio,
  ResourceType,
  VideoClipStatus,
  type IAudioGeneration,
  type IDialogueTone,
  type IImageGeneration,
  type IProjectAsset,
  type IVideoClip,
  type IVideoClipResource,
} from '@packages/global';
import {
  ChevronDownIcon,
  RectangleGroupIcon,
  MusicalNoteIcon,
  PhotoIcon,
  SpeakerWaveIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';

import { buildClipCanvasGraph } from './clipCanvasGraph';
import {
  AppliedMediaNode,
  CandidateResourceNode,
  ClipContextNode,
  FormGeneratorNode,
  MediaResourceNode,
  SettingAssetNode,
} from './nodes';
import { useClipCanvasContext } from './ClipCanvasContext';
import AudioTab from '../tabs/AudioTab';
import EffectsTab from '../tabs/EffectsTab';
import ImagesTab from '../tabs/ImagesTab';
import VideoTab from '../tabs/VideoTab';
import ProjectAssetSelector from '../../projectAssets/ProjectAssetSelector';
import AudioSelector from '../../common/AudioSelector';
import DialogueToneSelector from '../../common/DialogueToneSelector';
import EpisodeImageSelector from '../../common/EpisodeImageSelector';
import ImageSelector from '../../common/ImageSelector';
import { videoClipApi } from '../../../services/user/videoclip.api';
import { videoClipResourceApi } from '../../../services/user/videoClipResource.api';
import { imageUploadApi } from '../../../services/user/imageUpload.api';
import { projectAssetApi } from '../../../services/user/projectAsset.api';
import { useDialog } from '../../../hooks/useDialog';
import { useToast } from '../../../stores/toastStore';
import { getAudioDuration } from '../../../utils/audioUtils';
import { getVideoDuration } from '../../../utils/videoUtils';
import { videoFrameExtractor } from '../../../utils/videoFrameExtractor';
import {
  IMAGE_GENERATOR_NODE_ID,
  type ClipCanvasFormType,
  type ClipCanvasNode,
  type ClipCanvasNodeActions,
  type ClipCanvasResourceMediaType,
  type ClipImageTarget,
  type ClipPreviewEditorTarget,
} from './types';
import ClipCanvasPropertiesPanel from './ClipCanvasPropertiesPanel';
import { getAssetVoiceAudioUrl } from './referenceMedia';
import {
  REFERENCE_EDGE_STYLES,
  canAcceptReference,
  canAcceptReferenceHandle,
  getConnectableReference,
  getEdgeReference,
  getMediaTypeFromReferenceHandle,
  getReferenceEdgeClassName,
} from './clipCanvasReferences';
import {
  buildMediaResourceNode,
  buildSettingAssetNode,
  getDefaultMediaResourceNodePosition,
  getDefaultSettingNodePosition,
  isSettingAssetNode,
} from './clipCanvasNodeFactories';
import {
  buildRestoredSettingNodes,
  getLayoutStorageKey,
  getMaxFormNodeCounter,
  readSavedLayout,
  saveLayout,
  stableSerialize,
} from './clipCanvasLayoutStorage';

interface ClipCanvasFlowProps {
  videoClip: IVideoClip;
  resources: IVideoClipResource[];
}

const nodeTypes = {
  clipContext: ClipContextNode as ComponentType<any>,
  appliedMedia: AppliedMediaNode as ComponentType<any>,
  candidateResource: CandidateResourceNode as ComponentType<any>,
  formGenerator: FormGeneratorNode as ComponentType<any>,
  settingAsset: SettingAssetNode as ComponentType<any>,
  mediaResource: MediaResourceNode as ComponentType<any>,
};

const FORM_NODE_X = 0;
const FORM_NODE_Y = 720;
const FORM_NODE_GAP = 960;
const EPISODE_ASSET_LOAD_MAX_RETRIES = 3;

function getEpisodeAspectRatioCss(aspectRatio?: AspectRatio): string {
  switch (aspectRatio) {
    case AspectRatio.PORTRAIT:
      return '9 / 16';
    case AspectRatio.STANDARD:
      return '4 / 3';
    case AspectRatio.PORTRAIT_STANDARD:
      return '3 / 4';
    case AspectRatio.SQUARE:
      return '1 / 1';
    case AspectRatio.LANDSCAPE:
    default:
      return '16 / 9';
  }
}

const FORM_GROUPS: Array<{
  group: 'image' | 'audio' | 'video';
  label: string;
  icon: ComponentType<{ className?: string }>;
  items: Array<{
    type: ClipCanvasFormType;
    title: string;
    icon: ComponentType<{ className?: string }>;
  }>;
}> = [
  {
    group: 'image',
    label: '画面',
    icon: PhotoIcon,
    items: [
      { type: 'start-image', title: '开始帧生成', icon: PhotoIcon },
      { type: 'end-image', title: '结束帧生成', icon: PhotoIcon },
      { type: 'storyboard-image', title: '分镜图生成', icon: PhotoIcon },
    ],
  },
  {
    group: 'audio',
    label: '配音',
    icon: SpeakerWaveIcon,
    items: [
      { type: 'audio', title: '配音生成', icon: SpeakerWaveIcon },
      { type: 'audio-edit', title: '音频编辑', icon: MusicalNoteIcon },
    ],
  },
  {
    group: 'video',
    label: '视频',
    icon: VideoCameraIcon,
    items: [
      { type: 'video', title: '视频生成', icon: VideoCameraIcon },
      { type: 'lip-sync', title: '对口型生成', icon: VideoCameraIcon },
    ],
  },
];

const FORM_TEMPLATES: Array<{
  type: ClipCanvasFormType;
  title: string;
  icon: ComponentType<{ className?: string }>;
}> = FORM_GROUPS.flatMap((group) => group.items);

function buildReplacementClipTiming(
  currentDuration: number,
  currentTrimStart: number,
  currentTrimEnd: number | undefined,
  newVideoDuration: number,
) {
  const sourceTrimLength = Math.max(0, (currentTrimEnd ?? currentDuration) - currentTrimStart);
  const nextDuration = sourceTrimLength > 0 ? Math.min(sourceTrimLength, newVideoDuration) : newVideoDuration;
  return {
    nextDuration,
    nextTrimStart: 0,
    nextTrimEnd: Math.min(nextDuration, newVideoDuration),
  };
}

function getResourcePrompt(resource: IVideoClipResource): string | undefined {
  const params = getResourceGenerationParams(resource);
  const prompt = params?.prompt || params?.text;
  return typeof prompt === 'string' && prompt.trim() ? prompt : undefined;
}

function getResourceGenerationParams(resource: IVideoClipResource): Record<string, unknown> | undefined {
  const rawParams = resource.generationParams;
  if (!rawParams) return undefined;
  if (typeof rawParams === 'string') {
    try {
      const parsed = JSON.parse(rawParams) as unknown;
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed as Record<string, unknown>
        : undefined;
    } catch {
      return undefined;
    }
  }
  return rawParams && typeof rawParams === 'object' && !Array.isArray(rawParams)
    ? rawParams as Record<string, unknown>
    : undefined;
}

function getFormTypeFromResource(resourceType: ResourceType): ClipCanvasFormType | undefined {
  switch (resourceType) {
    case ResourceType.START_IMAGE:
      return 'start-image';
    case ResourceType.END_IMAGE:
      return 'end-image';
    case ResourceType.STORYBOARD_IMAGE:
      return 'storyboard-image';
    case ResourceType.SCRIPT_AUDIO:
    case ResourceType.SOUND_EFFECT_AUDIO:
      return 'audio';
    case ResourceType.AUDIO_EDIT:
      return 'audio-edit';
    case ResourceType.VIDEO:
      return 'video';
    case ResourceType.LIP_SYNC_VIDEO:
      return 'lip-sync';
    default:
      return undefined;
  }
}

function ClipCanvasFlowInner({ videoClip, resources }: ClipCanvasFlowProps) {
  const {
    episode,
    editConfig,
    onEditConfigChange,
    refreshVideoClip,
    requestInjectReference,
    onTriggerResourcePoll,
  } = useClipCanvasContext();
  const { confirm } = useDialog();
  const { success, error, warning } = useToast();
  const [editingNodeId, setEditingNodeId] = useState<string | null>(null);
  const [imageEditorFrameType, setImageEditorFrameType] = useState<ClipImageTarget>('start');
  const [imageEditorOpen, setImageEditorOpen] = useState(false);
  const [previewEditorTarget, setPreviewEditorTarget] = useState<ClipPreviewEditorTarget | null>(null);
  const [assetSelectorOpen, setAssetSelectorOpen] = useState(false);
  const [imageSelectorOpen, setImageSelectorOpen] = useState(false);
  const [episodeImageSelectorOpen, setEpisodeImageSelectorOpen] = useState(false);
  const [dialogueToneSelectorOpen, setDialogueToneSelectorOpen] = useState(false);
  const [audioSelectorOpen, setAudioSelectorOpen] = useState(false);
  const [connectingReferenceType, setConnectingReferenceType] = useState<ClipCanvasResourceMediaType | null>(null);
  const [episodeAssets, setEpisodeAssets] = useState<IProjectAsset[] | null>(null);
  const [episodeAssetsLoadAttempt, setEpisodeAssetsLoadAttempt] = useState(0);
  const [autoLoadRetryTick, setAutoLoadRetryTick] = useState(0);
  const formNodeCounterRef = useRef(0);
  const autoLoadEpisodeCharactersRef = useRef(false);
  const skipInitialGraphSyncRef = useRef(true);
  const nodesRef = useRef<ClipCanvasNode[]>([]);
  const onUseAsReferenceRef = useRef<ClipCanvasNodeActions['onUseAsReference']>(() => undefined);
  const onEditNodeRef = useRef<NonNullable<ClipCanvasNodeActions['onEditNode']>>(() => undefined);
  const onEditImageFrameRef = useRef<NonNullable<ClipCanvasNodeActions['onEditImageFrame']>>(() => undefined);
  const onEditPreviewPanelRef = useRef<NonNullable<ClipCanvasNodeActions['onEditPreviewPanel']>>(() => undefined);
  const onApplyImageResourceRef = useRef<NonNullable<ClipCanvasNodeActions['onApplyImageResource']>>(async () => undefined);
  const onApplyAudioResourceRef = useRef<NonNullable<ClipCanvasNodeActions['onApplyAudioResource']>>(async () => undefined);
  const onApplyVideoResourceRef = useRef<NonNullable<ClipCanvasNodeActions['onApplyVideoResource']>>(async () => undefined);
  const onCopyResourcePromptRef = useRef<NonNullable<ClipCanvasNodeActions['onCopyResourcePrompt']>>(async () => undefined);
  const onDeleteResourceRef = useRef<NonNullable<ClipCanvasNodeActions['onDeleteResource']>>(async () => undefined);
  const deleteFormNodeRef = useRef<NonNullable<ClipCanvasNodeActions['onDeleteFormNode']>>(async () => undefined);
  const removeFormNodeRef = useRef<NonNullable<ClipCanvasNodeActions['onRemoveFormNode']>>(async () => undefined);
  const createFormFromResourceRef = useRef<NonNullable<ClipCanvasNodeActions['onCreateFormFromResource']>>(async () => undefined);
  const deleteSettingNodeRef = useRef<NonNullable<ClipCanvasNodeActions['onDeleteSettingNode']>>(async () => undefined);
  const deleteMediaResourceNodeRef = useRef<NonNullable<ClipCanvasNodeActions['onDeleteMediaResourceNode']>>(async () => undefined);
  const referenceMediaClearedRef = useRef<NonNullable<ClipCanvasNodeActions['onReferenceMediaCleared']>>(() => undefined);
  const graphInputsRef = useRef({ videoClip, resources, mediaAspectRatio: episode?.videoAspectRatio });
  const storeApi = useStoreApi<ClipCanvasNode, Edge>();
  const layoutStorageKey = useMemo(
    () => getLayoutStorageKey(videoClip),
    [videoClip.episodeId, videoClip.id, videoClip.projectId],
  );
  const savedLayout = useMemo(() => readSavedLayout(layoutStorageKey), [layoutStorageKey]);
  const canvasMediaAspectRatio = useMemo(
    () => getEpisodeAspectRatioCss(episode?.videoAspectRatio),
    [episode?.videoAspectRatio],
  );
  const flowStyle = useMemo<React.CSSProperties & Record<'--clip-canvas-media-aspect-ratio', string>>(
    () => ({ '--clip-canvas-media-aspect-ratio': canvasMediaAspectRatio }),
    [canvasMediaAspectRatio],
  );
  const savedPreviousNodes = useMemo<ClipCanvasNode[] | undefined>(() => {
    if (!savedLayout) return undefined;
    return Object.entries(savedLayout.positions).map(([id, position]) => ({
      id,
      position,
      data: { kind: 'form-generator', formType: 'video', title: '' },
    } as ClipCanvasNode));
  }, [savedLayout]);

  useEffect(() => {
    formNodeCounterRef.current = Math.max(
      formNodeCounterRef.current,
      getMaxFormNodeCounter(savedLayout?.formNodes),
    );
  }, [savedLayout]);

  useEffect(() => {
    graphInputsRef.current = { videoClip, resources, mediaAspectRatio: episode?.videoAspectRatio };
  }, [episode?.videoAspectRatio, resources, videoClip]);

  // 把"媒体节点 -> 生成器节点"的连线请求转成参考媒体注入
  const onUseAsReference = useCallback<ClipCanvasNodeActions['onUseAsReference']>(
    (url, target = IMAGE_GENERATOR_NODE_ID) => {
      requestInjectReference(target, url, 'image');
    },
    [requestInjectReference],
  );

  const onEditNode = useCallback<NonNullable<ClipCanvasNodeActions['onEditNode']>>((nodeId) => {
    setEditingNodeId(nodeId);
  }, []);

  const onEditImageFrame = useCallback<NonNullable<ClipCanvasNodeActions['onEditImageFrame']>>((target) => {
    setImageEditorFrameType(target);
    setImageEditorOpen(true);
  }, []);

  const onEditPreviewPanel = useCallback<NonNullable<ClipCanvasNodeActions['onEditPreviewPanel']>>((target) => {
    setPreviewEditorTarget(target);
  }, []);

  const onApplyImageResource = useCallback<NonNullable<ClipCanvasNodeActions['onApplyImageResource']>>(
    async (imageUrl, target) => {
      const updateData = target === 'start'
        ? { startImage: imageUrl, status: VideoClipStatus.COMPLETED }
        : target === 'end'
          ? { endImage: imageUrl, status: VideoClipStatus.COMPLETED }
          : { storyboardImage: imageUrl, status: VideoClipStatus.COMPLETED };

      try {
        await videoClipApi.updateVideoClip(videoClip.id, updateData);
        await refreshVideoClip?.(videoClip.id);
        const labels: Record<ClipImageTarget, string> = {
          start: '已应用到开始帧',
          end: '已应用到结束帧',
          storyboard: '已应用到分镜图',
        };
        success(labels[target]);
      } catch (err) {
        console.error('画布应用图片失败:', err);
        error('应用图片失败');
      }
    },
    [error, refreshVideoClip, success, videoClip.id],
  );

  const onApplyAudioResource = useCallback<NonNullable<ClipCanvasNodeActions['onApplyAudioResource']>>(
    async (audioUrl) => {
      try {
        let audioDuration: number | undefined;
        try {
          audioDuration = await getAudioDuration(audioUrl);
        } catch {
          audioDuration = undefined;
        }

        const updateData: Record<string, unknown> = {
          scriptAudioUrl: audioUrl,
          status: VideoClipStatus.COMPLETED,
        };
        if (audioDuration && audioDuration > 0 && !videoClip.videoUrl && audioDuration > (videoClip.duration || 0)) {
          updateData.duration = audioDuration;
        }

        await videoClipApi.updateVideoClip(videoClip.id, updateData);
        await refreshVideoClip?.(videoClip.id);
        await onTriggerResourcePoll?.();
        success('配音已应用');
      } catch (err) {
        console.error('画布应用配音失败:', err);
        error('应用配音失败');
      }
    },
    [error, onTriggerResourcePoll, refreshVideoClip, success, videoClip.duration, videoClip.id, videoClip.videoUrl],
  );

  const onApplyVideoResource = useCallback<NonNullable<ClipCanvasNodeActions['onApplyVideoResource']>>(
    async (videoUrl) => {
      try {
        const videoDuration = await getVideoDuration(videoUrl);
        const clipInfo = editConfig?.clips.find((clip) => clip.clipId === videoClip.id);
        const currentDuration = clipInfo?.duration || videoClip.duration || 5;
        const currentTrimStart = clipInfo?.trimStart || 0;
        const currentTrimEnd = clipInfo?.trimEnd;
        const { nextDuration, nextTrimStart, nextTrimEnd } = buildReplacementClipTiming(
          currentDuration,
          currentTrimStart,
          currentTrimEnd,
          videoDuration,
        );

        let extractedStartImageUrl: string | undefined;
        if (!videoClip.startImage) {
          try {
            const frameFile = await videoFrameExtractor.extractFrameAsFile(
              videoUrl,
              `clip-${videoClip.id}-first-frame.jpg`,
              0,
            );
            const uploaded = await imageUploadApi.uploadImage(frameFile);
            extractedStartImageUrl = uploaded?.url;
          } catch (err) {
            console.error('画布应用视频抽取首帧失败:', err);
            warning('视频已应用，首帧提取失败');
          }
        }

        await videoClipApi.updateVideoClip(videoClip.id, {
          videoUrl,
          status: VideoClipStatus.COMPLETED,
          ...(extractedStartImageUrl ? { startImage: extractedStartImageUrl } : {}),
        });
        await refreshVideoClip?.(videoClip.id);

        if (editConfig && onEditConfigChange) {
          const clipIndex = editConfig.clips.findIndex((clip) => clip.clipId === videoClip.id);
          if (clipIndex !== -1) {
            const oldClipDuration = editConfig.clips[clipIndex].duration;
            const durationDiff = nextDuration - oldClipDuration;
            const updatedClips = editConfig.clips.map((clip, index) => {
              if (index === clipIndex) {
                return {
                  ...clip,
                  duration: nextDuration,
                  trimStart: nextTrimStart,
                  trimEnd: nextTrimEnd,
                };
              }
              if (index > clipIndex) {
                return {
                  ...clip,
                  startTime: clip.startTime + durationDiff,
                };
              }
              return clip;
            });

            onEditConfigChange({
              ...editConfig,
              clips: updatedClips,
              timeline: {
                ...editConfig.timeline,
                totalDuration: editConfig.timeline.totalDuration + durationDiff,
              },
            });
          }
        }

        await onTriggerResourcePoll?.();
        success('视频已应用');
      } catch (err) {
        console.error('画布应用视频失败:', err);
        error('应用视频失败');
      }
    },
    [editConfig, error, onEditConfigChange, onTriggerResourcePoll, refreshVideoClip, success, videoClip.duration, videoClip.id, videoClip.startImage, warning],
  );

  const onCopyResourcePrompt = useCallback<NonNullable<ClipCanvasNodeActions['onCopyResourcePrompt']>>(
    async (resource) => {
      try {
        const prompt = getResourcePrompt(resource);
        if (!prompt) {
          error('没有可复制的提示词');
          return;
        }
        await navigator.clipboard.writeText(prompt);
        success('提示词已复制');
      } catch (err) {
        console.error('画布复制提示词失败:', err);
        error('复制提示词失败');
      }
    },
    [error, success],
  );

  const onDeleteResource = useCallback<NonNullable<ClipCanvasNodeActions['onDeleteResource']>>(
    async (resourceId) => {
      const confirmed = await confirm('确定删除这个候选资源吗？', '删除候选资源', 'danger');
      if (!confirmed) return;

      try {
        await videoClipResourceApi.deleteResource(resourceId);
        await onTriggerResourcePoll?.();
        success('候选资源已删除');
      } catch (err) {
        console.error('画布删除候选资源失败:', err);
        error('删除候选资源失败');
      }
    },
    [confirm, error, onTriggerResourcePoll, success],
  );

  useEffect(() => {
    onUseAsReferenceRef.current = onUseAsReference;
    onEditNodeRef.current = onEditNode;
    onEditImageFrameRef.current = onEditImageFrame;
    onEditPreviewPanelRef.current = onEditPreviewPanel;
    onApplyImageResourceRef.current = onApplyImageResource;
    onApplyAudioResourceRef.current = onApplyAudioResource;
    onApplyVideoResourceRef.current = onApplyVideoResource;
    onCopyResourcePromptRef.current = onCopyResourcePrompt;
    onDeleteResourceRef.current = onDeleteResource;
  }, [
    onApplyAudioResource,
    onApplyImageResource,
    onApplyVideoResource,
    onCopyResourcePrompt,
    onDeleteResource,
    onEditImageFrame,
    onEditNode,
    onEditPreviewPanel,
    onUseAsReference,
  ]);

  // 保持节点 data.actions 引用稳定；所有行为走 ref 委托，避免 retained nodes 持有陈旧闭包。
  const actions = useMemo<ClipCanvasNodeActions>(
    () => ({
      onUseAsReference: (url, target) => onUseAsReferenceRef.current(url, target),
      onEditNode: (nodeId) => onEditNodeRef.current(nodeId),
      onEditImageFrame: (target) => onEditImageFrameRef.current(target),
      onEditPreviewPanel: (target) => onEditPreviewPanelRef.current(target),
      onApplyImageResource: (imageUrl, target) => onApplyImageResourceRef.current(imageUrl, target),
      onApplyAudioResource: (audioUrl) => onApplyAudioResourceRef.current(audioUrl),
      onApplyVideoResource: (videoUrl) => onApplyVideoResourceRef.current(videoUrl),
      onCreateFormFromResource: (resource) => createFormFromResourceRef.current(resource),
      onCopyResourcePrompt: (resource) => onCopyResourcePromptRef.current(resource),
      onDeleteResource: (resourceId) => onDeleteResourceRef.current(resourceId),
      onDeleteFormNode: (nodeId) => deleteFormNodeRef.current(nodeId),
      onRemoveFormNode: (nodeId) => removeFormNodeRef.current(nodeId),
      onDeleteSettingNode: (nodeId) => deleteSettingNodeRef.current(nodeId),
      onDeleteMediaResourceNode: (nodeId) => deleteMediaResourceNodeRef.current(nodeId),
      onReferenceMediaCleared: (targetNodeId, url, mediaType) => referenceMediaClearedRef.current(targetNodeId, url, mediaType),
    }),
    [],
  );

  const initialGraph = useMemo(
    () => {
      const graph = buildClipCanvasGraph({
        videoClip,
        resources,
        actions,
        previousNodes: savedPreviousNodes,
        mediaAspectRatio: episode?.videoAspectRatio,
      });
      const restoredFormNodes = (savedLayout?.formNodes ?? []).map((formNode) => ({
        id: formNode.id,
        type: 'formGenerator',
        position: formNode.position,
        data: {
          kind: 'form-generator',
          formType: formNode.type,
          title: formNode.title,
          initialParams: formNode.initialParams,
          actions,
        },
      } as ClipCanvasNode));
      const restoredMediaResourceNodes = (savedLayout?.mediaResourceNodes ?? []).map((resourceNode) => ({
        id: resourceNode.id,
        type: 'mediaResource',
        position: resourceNode.position,
        data: {
          kind: 'media-resource',
          mediaType: resourceNode.mediaType,
          title: resourceNode.title,
          url: resourceNode.url,
          sourceLabel: resourceNode.sourceLabel,
          prompt: resourceNode.prompt,
          actions,
        },
      } as ClipCanvasNode));
      return {
        nodes: [...graph.nodes, ...restoredFormNodes, ...restoredMediaResourceNodes],
        edges: graph.edges,
      };
    },
    // 仅初始化一次；后续靠下面的 effect 增量重建
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialGraph.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialGraph.edges);
  // 用户手动拉的"参考图"连线，与派生连线分开保存，重建时合并保留
  const userEdgesRef = useRef<Edge[]>([]);

  useEffect(() => {
    nodesRef.current = nodes as ClipCanvasNode[];
  }, [nodes]);

  const removeFormNode = useCallback<NonNullable<ClipCanvasNodeActions['onRemoveFormNode']>>(
    async (nodeId) => {
      setNodes((currentNodes) => {
        const nextNodes = (currentNodes as ClipCanvasNode[]).filter((node) => node.id !== nodeId);
        saveLayout(layoutStorageKey, nextNodes);
        return nextNodes;
      });
      setEdges((currentEdges: Edge[]) => {
        const nextEdges = currentEdges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId);
        userEdgesRef.current = userEdgesRef.current.filter((edge) => edge.source !== nodeId && edge.target !== nodeId);
        return nextEdges;
      });
    },
    [layoutStorageKey, setEdges, setNodes],
  );

  useEffect(() => {
    removeFormNodeRef.current = removeFormNode;
  }, [removeFormNode]);

  const handleDeleteFormNode = useCallback<NonNullable<ClipCanvasNodeActions['onDeleteFormNode']>>(
    async (nodeId) => {
      const confirmed = await confirm('确定删除这个表单节点吗？表单中尚未提交的内容会丢失。', '删除表单节点', 'warning');
      if (!confirmed) return;

      await removeFormNode(nodeId);
      success('表单节点已删除');
    },
    [confirm, removeFormNode, success],
  );

  useEffect(() => {
    deleteFormNodeRef.current = handleDeleteFormNode;
  }, [handleDeleteFormNode]);

  const handleDeleteSettingNode = useCallback<NonNullable<ClipCanvasNodeActions['onDeleteSettingNode']>>(
    async (nodeId) => {
      const confirmed = await confirm('确定删除这个设定节点吗？', '删除设定节点', 'warning');
      if (!confirmed) return;

      setNodes((currentNodes) => {
        const nextNodes = (currentNodes as ClipCanvasNode[]).filter((node) => node.id !== nodeId);
        saveLayout(layoutStorageKey, nextNodes);
        return nextNodes;
      });
      setEdges((currentEdges: Edge[]) => {
        const nextEdges = currentEdges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId);
        userEdgesRef.current = userEdgesRef.current.filter((edge) => edge.source !== nodeId && edge.target !== nodeId);
        return nextEdges;
      });
      success('设定节点已删除');
    },
    [confirm, layoutStorageKey, setEdges, setNodes, success],
  );

  useEffect(() => {
    deleteSettingNodeRef.current = handleDeleteSettingNode;
  }, [handleDeleteSettingNode]);

  const handleDeleteMediaResourceNode = useCallback<NonNullable<ClipCanvasNodeActions['onDeleteMediaResourceNode']>>(
    async (nodeId) => {
      const confirmed = await confirm('确定删除这个资源节点吗？', '删除资源节点', 'warning');
      if (!confirmed) return;

      setNodes((currentNodes) => {
        const nextNodes = (currentNodes as ClipCanvasNode[]).filter((node) => node.id !== nodeId);
        saveLayout(layoutStorageKey, nextNodes);
        return nextNodes;
      });
      setEdges((currentEdges: Edge[]) => {
        const nextEdges = currentEdges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId);
        userEdgesRef.current = userEdgesRef.current.filter((edge) => edge.source !== nodeId && edge.target !== nodeId);
        return nextEdges;
      });
      success('资源节点已删除');
    },
    [confirm, layoutStorageKey, setEdges, setNodes, success],
  );

  useEffect(() => {
    deleteMediaResourceNodeRef.current = handleDeleteMediaResourceNode;
  }, [handleDeleteMediaResourceNode]);

  const handleReferenceMediaCleared = useCallback<NonNullable<ClipCanvasNodeActions['onReferenceMediaCleared']>>(
    (targetNodeId, url, mediaType) => {
      setEdges((currentEdges: Edge[]) => {
        const currentNodes = nodesRef.current;
        const nextEdges = currentEdges.filter((edge) => {
          if (edge.target !== targetNodeId) return true;
          const reference = getEdgeReference(edge, currentNodes);
          if (!reference) return true;
          return !(reference.mediaType === mediaType && reference.url === url);
        });
        userEdgesRef.current = userEdgesRef.current.filter((edge) => nextEdges.some((nextEdge) => nextEdge.id === edge.id));
        return nextEdges;
      });
    },
    [setEdges],
  );

  useEffect(() => {
    referenceMediaClearedRef.current = handleReferenceMediaCleared;
  }, [handleReferenceMediaCleared]);

  const editingNode = useMemo(
    () => nodes.find((node) => node.id === editingNodeId) as ClipCanvasNode | undefined,
    [editingNodeId, nodes],
  );

  const clipGraphSignature = useMemo(
    () => stableSerialize({
      id: videoClip.id,
      sequence: videoClip.sequence,
      title: videoClip.title,
      sceneName: videoClip.sceneName,
      speaker: videoClip.speaker,
      duration: videoClip.duration,
      description: videoClip.description,
      script: videoClip.script,
      structure: videoClip.structure,
      cameraWork: videoClip.cameraWork,
      transition: videoClip.transition,
      startImage: videoClip.startImage,
      endImage: videoClip.endImage,
      storyboardImage: videoClip.storyboardImage,
      scriptAudioUrl: videoClip.scriptAudioUrl,
      videoUrl: videoClip.videoUrl,
      effectItems: videoClip.effectItems,
    }),
    [
      videoClip.cameraWork,
      videoClip.description,
      videoClip.duration,
      videoClip.effectItems,
      videoClip.endImage,
      videoClip.id,
      videoClip.sceneName,
      videoClip.script,
      videoClip.scriptAudioUrl,
      videoClip.sequence,
      videoClip.speaker,
      videoClip.startImage,
      videoClip.storyboardImage,
      videoClip.structure,
      videoClip.title,
      videoClip.transition,
      videoClip.videoUrl,
    ],
  );

  const resourceGraphSignature = useMemo(
    () => resources
      .map((resource) => [
        resource.id,
        resource.resourceType,
        resource.status,
        resource.resultUrl,
        resource.errorMessage,
        resource.imageGenerationId,
        resource.imageEditId,
        resource.audioGenerationId,
        resource.videoGenerationId,
        stableSerialize(resource.generationParams),
      ].join('|'))
      .join('\n'),
    [resources],
  );

  useEffect(() => {
    if (skipInitialGraphSyncRef.current) {
      skipInitialGraphSyncRef.current = false;
      return;
    }

    const currentNodes = nodesRef.current;
    const {
      videoClip: currentVideoClip,
      resources: currentResources,
      mediaAspectRatio: currentMediaAspectRatio,
    } = graphInputsRef.current;
    const nextGraph = buildClipCanvasGraph({
      videoClip: currentVideoClip,
      resources: currentResources,
      actions,
      previousNodes: [
        ...currentNodes,
        ...(savedPreviousNodes ?? []),
      ],
      mediaAspectRatio: currentMediaAspectRatio,
    });
    const retainedNodes = currentNodes.filter(
      (node) => node.data.kind === 'form-generator'
        || node.data.kind === 'setting-asset'
        || node.data.kind === 'media-resource',
    );
    setNodes([...nextGraph.nodes, ...retainedNodes]);
    setEdges([...nextGraph.edges, ...userEdgesRef.current]);
  }, [actions, clipGraphSignature, resourceGraphSignature, savedPreviousNodes, setEdges, setNodes]);

  const persistCurrentLayout = useCallback(() => {
    const currentNodes = storeApi.getState().nodes as ClipCanvasNode[];
    saveLayout(layoutStorageKey, currentNodes);
  }, [layoutStorageKey, storeApi]);

  const handleNodesChange = useCallback(
    (changes: NodeChange<ClipCanvasNode>[]) => {
      onNodesChange(changes);
      if (changes.some((change) => change.type === 'position' && !change.dragging)) {
        window.setTimeout(persistCurrentLayout, 0);
      }
    },
    [onNodesChange, persistCurrentLayout],
  );

  const addFormNode = useCallback((formType: ClipCanvasFormType, initialParams?: Record<string, unknown>) => {
    const template = FORM_TEMPLATES.find((item) => item.type === formType);
    if (!template) return;

    formNodeCounterRef.current += 1;
    const nodeId = `form:${videoClip.id}:${formType}:${formNodeCounterRef.current}`;

    setNodes((currentNodes) => {
      const existingFormCount = (currentNodes as ClipCanvasNode[])
        .filter((node) => node.data.kind === 'form-generator')
        .length;
      const nextNodes = [
        ...currentNodes,
        {
          id: nodeId,
          type: 'formGenerator',
          position: {
            x: FORM_NODE_X,
            y: FORM_NODE_Y + existingFormCount * FORM_NODE_GAP,
          },
          data: {
            kind: 'form-generator',
            formType,
            title: template.title,
            initialParams,
            actions,
          },
        } as ClipCanvasNode,
      ];
      saveLayout(layoutStorageKey, nextNodes as ClipCanvasNode[]);
      return nextNodes;
    });
  }, [actions, layoutStorageKey, setNodes, videoClip.id]);

  const handleCreateFormFromResource = useCallback<NonNullable<ClipCanvasNodeActions['onCreateFormFromResource']>>(
    async (resource) => {
      const formType = getFormTypeFromResource(resource.resourceType);
      const initialParams = getResourceGenerationParams(resource);
      if (!formType || !initialParams) {
        warning('该候选资源没有可复用的表单参数');
        return;
      }

      addFormNode(formType, initialParams);
      success('已基于候选参数创建表单');
    },
    [addFormNode, success, warning],
  );

  useEffect(() => {
    createFormFromResourceRef.current = handleCreateFormFromResource;
  }, [handleCreateFormFromResource]);

  const addSettingNode = useCallback((asset: IProjectAsset, imageUrl?: string) => {
    setNodes((currentNodes) => {
      const existingSettingCount = (currentNodes as ClipCanvasNode[])
        .filter((node) => node.data.kind === 'setting-asset')
        .length;
      const nextNodes = [
        ...currentNodes,
        buildSettingAssetNode(
          videoClip.id,
          asset,
          getDefaultSettingNodePosition(existingSettingCount),
          actions,
          imageUrl || asset.conceptImageUrl,
          getAssetVoiceAudioUrl(asset),
        ),
      ];
      saveLayout(layoutStorageKey, nextNodes as ClipCanvasNode[]);
      return nextNodes;
    });
    success('设定已添加到画布');
  }, [actions, layoutStorageKey, setNodes, success, videoClip.id]);

  const addMediaResourceNode = useCallback((
    mediaType: ClipCanvasResourceMediaType,
    title: string,
    url: string,
    sourceLabel: string,
    prompt?: string,
  ) => {
    if (!url) {
      warning('资源地址为空，无法添加到画布');
      return;
    }

    setNodes((currentNodes) => {
      const existingResourceCount = (currentNodes as ClipCanvasNode[])
        .filter((node) => node.data.kind === 'media-resource')
        .length;
      const nextNodes = [
        ...currentNodes,
        buildMediaResourceNode(
          videoClip.id,
          mediaType,
          title,
          url,
          sourceLabel,
          getDefaultMediaResourceNodePosition(existingResourceCount),
          actions,
          prompt,
        ),
      ];
      saveLayout(layoutStorageKey, nextNodes as ClipCanvasNode[]);
      return nextNodes;
    });
    success(mediaType === 'image' ? '图片资源已添加到画布' : '音频资源已添加到画布');
  }, [actions, layoutStorageKey, setNodes, success, videoClip.id, warning]);

  useEffect(() => {
    setEpisodeAssets(null);
    setEpisodeAssetsLoadAttempt(0);
    setAutoLoadRetryTick(0);
    autoLoadEpisodeCharactersRef.current = false;
  }, [videoClip.episodeId]);

  useEffect(() => {
    if (!videoClip.episodeId) {
      setEpisodeAssets([]);
      return;
    }

    let cancelled = false;
    let retryTimer: number | undefined;
    const loadEpisodeAssets = async () => {
      try {
        const assets = await projectAssetApi.getEpisodeAssets(videoClip.episodeId);
        if (!cancelled) {
          setEpisodeAssets(assets);
        }
      } catch (err) {
        console.error('画布加载剧集资源失败:', err);
        if (!cancelled && episodeAssetsLoadAttempt < EPISODE_ASSET_LOAD_MAX_RETRIES) {
          retryTimer = window.setTimeout(() => {
            setEpisodeAssetsLoadAttempt((attempt) => attempt + 1);
          }, 1500);
        }
      }
    };

    void loadEpisodeAssets();

    return () => {
      cancelled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [episodeAssetsLoadAttempt, videoClip.episodeId]);

  useEffect(() => {
    if (!episodeAssets || !savedLayout?.settingNodes?.length) return;

    setNodes((currentNodes) => {
      const currentClipNodes = currentNodes as ClipCanvasNode[];
      const existingNodeIds = new Set(currentClipNodes.map((node) => node.id));
      const restoredSettingNodes = buildRestoredSettingNodes(savedLayout, episodeAssets, videoClip.id, actions)
        .filter((node) => !existingNodeIds.has(node.id));
      if (restoredSettingNodes.length === 0) return currentNodes;

      const nextNodes = [...currentClipNodes, ...restoredSettingNodes];
      saveLayout(layoutStorageKey, nextNodes);
      return nextNodes;
    });
  }, [actions, episodeAssets, layoutStorageKey, savedLayout, setNodes, videoClip.id]);

  useEffect(() => {
    if (savedLayout?.episodeCharactersAutoLoaded || autoLoadEpisodeCharactersRef.current || !videoClip.episodeId || !episodeAssets) return;

    autoLoadEpisodeCharactersRef.current = true;
    let retryTimer: number | undefined;

    try {
      const characterAssets = episodeAssets.filter((asset) => asset.assetType === AssetType.CHARACTER);
      if (characterAssets.length === 0) {
        const currentNodes = storeApi.getState().nodes as ClipCanvasNode[];
        saveLayout(layoutStorageKey, currentNodes, { episodeCharactersAutoLoaded: true });
        return;
      }

      setNodes((currentNodes) => {
        const currentClipNodes = currentNodes as ClipCanvasNode[];
        const existingSettingAssetIds = new Set(
          currentClipNodes
            .filter(isSettingAssetNode)
            .map((node) => node.data.asset.id),
        );
        const autoNodes = characterAssets
          .filter((asset) => !existingSettingAssetIds.has(asset.id))
          .map((asset, index) => buildSettingAssetNode(
            videoClip.id,
            asset,
            getDefaultSettingNodePosition(existingSettingAssetIds.size + index),
            actions,
            asset.conceptImageUrl,
            getAssetVoiceAudioUrl(asset),
            `episode-character-${index}`,
          ));

        if (autoNodes.length === 0) {
          saveLayout(layoutStorageKey, currentClipNodes, { episodeCharactersAutoLoaded: true });
          return currentNodes;
        }

        const nextNodes = [...currentClipNodes, ...autoNodes];
        saveLayout(layoutStorageKey, nextNodes, { episodeCharactersAutoLoaded: true });
        return nextNodes;
      });
    } catch (err) {
      console.error('画布自动加载剧集角色失败:', err);
      autoLoadEpisodeCharactersRef.current = false;
      retryTimer = window.setTimeout(() => {
        setAutoLoadRetryTick((tick) => tick + 1);
      }, 1500);
    }

    return () => {
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [actions, autoLoadRetryTick, episodeAssets, layoutStorageKey, savedLayout?.episodeCharactersAutoLoaded, setNodes, storeApi, videoClip.episodeId, videoClip.id]);

  const onConnect = useCallback(
    (connection: Connection) => {
      const { source, sourceHandle, target, targetHandle } = connection;
      if (!source || !target) return;

      const currentNodes = nodesRef.current;
      const sourceNode = currentNodes.find((node) => node.id === source) as ClipCanvasNode | undefined;
      const targetNode = currentNodes.find((node) => node.id === target) as ClipCanvasNode | undefined;
      if (!sourceNode) return;
      const reference = getConnectableReference(sourceNode, sourceHandle);
      if (!reference) return;
      if (!canAcceptReference(targetNode, reference.mediaType)) return;
      if (!canAcceptReferenceHandle(targetHandle, reference.mediaType)) return;

      setEdges((current) => {
        const filtered = reference.mediaType === 'audio'
          ? current.filter((edge) => {
            if (edge.target !== target) return true;
            const edgeReference = getEdgeReference(edge, currentNodes);
            return edgeReference?.mediaType !== 'audio';
          })
          : current;
        const next = addEdge({
          ...connection,
          animated: true,
          className: getReferenceEdgeClassName(reference.mediaType),
          style: REFERENCE_EDGE_STYLES[reference.mediaType],
        }, filtered);
        // 同步记录用户连线，确保后续重建时保留
        userEdgesRef.current = next.filter(
          (edge) => !edge.id.startsWith(`edge:clip:${videoClip.id}:`),
        );
        return next;
      });
      requestInjectReference(target, reference.url, reference.mediaType);
    },
    [requestInjectReference, setEdges, videoClip.id],
  );

  const onConnectStart = useCallback((
    _event: MouseEvent | TouchEvent,
    params: { handleId?: string | null },
  ) => {
    setConnectingReferenceType(getMediaTypeFromReferenceHandle(params.handleId));
  }, []);

  const onConnectEnd = useCallback(() => {
    setConnectingReferenceType(null);
  }, []);

  const connectionLineStyle = useMemo<CSSProperties | undefined>(
    () => connectingReferenceType ? REFERENCE_EDGE_STYLES[connectingReferenceType] : undefined,
    [connectingReferenceType],
  );

  return (
    <ReactFlow
      style={flowStyle}
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      fitView
      fitViewOptions={{ padding: 0.2 }}
      minZoom={0.2}
      maxZoom={1.5}
      onNodesChange={handleNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={onConnect}
      onConnectStart={onConnectStart}
      onConnectEnd={onConnectEnd}
      connectionLineStyle={connectionLineStyle}
      proOptions={{ hideAttribution: true }}
    >
      <Background color="var(--clip-canvas-grid-line)" gap={24} />
      <Controls />
      <MiniMap pannable zoomable nodeStrokeWidth={3} />
      <div className="clip-canvas-form-toolbar nodrag nopan nowheel">
        {FORM_GROUPS.map((group) => {
          const GroupIcon = group.icon;
          return (
            <div key={group.group} className="clip-canvas-form-toolbar__group">
              <button type="button" className="clip-canvas-form-toolbar__trigger">
                <GroupIcon className="clip-canvas-form-toolbar__icon" />
                <span>{group.label}</span>
                <ChevronDownIcon className="clip-canvas-form-toolbar__chevron" />
              </button>
              <div className="clip-canvas-form-toolbar__menu">
                {group.items.map((template) => {
                  const Icon = template.icon;
                  return (
                    <button
                      key={template.type}
                      type="button"
                      className="clip-canvas-form-toolbar__item"
                      onClick={() => addFormNode(template.type)}
                    >
                      <Icon className="clip-canvas-form-toolbar__icon" />
                      <span>{template.title}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
        <button
          type="button"
          className="clip-canvas-form-toolbar__trigger"
          onClick={() => setAssetSelectorOpen(true)}
        >
          <RectangleGroupIcon className="clip-canvas-form-toolbar__icon" />
          <span>设定</span>
        </button>
        <div className="clip-canvas-form-toolbar__group">
          <button type="button" className="clip-canvas-form-toolbar__trigger">
            <RectangleGroupIcon className="clip-canvas-form-toolbar__icon" />
            <span>资源</span>
            <ChevronDownIcon className="clip-canvas-form-toolbar__chevron" />
          </button>
          <div className="clip-canvas-form-toolbar__menu">
            <button
              type="button"
              className="clip-canvas-form-toolbar__item"
              onClick={() => setImageSelectorOpen(true)}
            >
              <PhotoIcon className="clip-canvas-form-toolbar__icon" />
              <span>系统图库</span>
            </button>
            <button
              type="button"
              className="clip-canvas-form-toolbar__item"
              onClick={() => setEpisodeImageSelectorOpen(true)}
            >
              <PhotoIcon className="clip-canvas-form-toolbar__icon" />
              <span>剧集图库</span>
            </button>
            <button
              type="button"
              className="clip-canvas-form-toolbar__item"
              onClick={() => setDialogueToneSelectorOpen(true)}
            >
              <SpeakerWaveIcon className="clip-canvas-form-toolbar__icon" />
              <span>参考音色</span>
            </button>
            <button
              type="button"
              className="clip-canvas-form-toolbar__item"
              onClick={() => setAudioSelectorOpen(true)}
            >
              <MusicalNoteIcon className="clip-canvas-form-toolbar__icon" />
              <span>音频资源</span>
            </button>
          </div>
        </div>
      </div>
      <ClipCanvasPropertiesPanel
        node={editingNode ?? null}
        onClose={() => setEditingNodeId(null)}
      />
      <ProjectAssetSelector
        isOpen={assetSelectorOpen}
        projectId={videoClip.projectId}
        episodeId={videoClip.episodeId}
        initialAspect="image"
        onSelect={(asset: IProjectAsset, value: string) => {
          addSettingNode(asset, value);
          setAssetSelectorOpen(false);
        }}
        onClose={() => setAssetSelectorOpen(false)}
      />
      <ImageSelector
        isOpen={imageSelectorOpen}
        models={[]}
        onSelect={(image: IImageGeneration) => {
          if (!image.resultImageUrl) return;
          addMediaResourceNode('image', '系统图库图片', image.resultImageUrl, '系统图库', image.prompt);
          setImageSelectorOpen(false);
        }}
        onClose={() => setImageSelectorOpen(false)}
      />
      <EpisodeImageSelector
        isOpen={episodeImageSelectorOpen}
        episodeId={videoClip.episodeId}
        onSelect={(imageUrl: string, resource: IVideoClipResource) => {
          addMediaResourceNode('image', '剧集图库图片', imageUrl, '剧集图库', getResourcePrompt(resource));
          setEpisodeImageSelectorOpen(false);
        }}
        onClose={() => setEpisodeImageSelectorOpen(false)}
      />
      <DialogueToneSelector
        isOpen={dialogueToneSelectorOpen}
        onSelect={(tone: IDialogueTone) => {
          if (!tone.previewAudioUrl) return;
          const title = tone.desc || [tone.languageDesc, tone.genderDesc, tone.ageGroupDesc].filter(Boolean).join(' ');
          addMediaResourceNode('audio', title || '参考音色', tone.previewAudioUrl, '参考音色');
          setDialogueToneSelectorOpen(false);
        }}
        onClose={() => setDialogueToneSelectorOpen(false)}
      />
      <AudioSelector
        isOpen={audioSelectorOpen}
        onSelect={(audio: IAudioGeneration) => {
          if (!audio.resultAudioUrl) return;
          addMediaResourceNode('audio', '音频资源', audio.resultAudioUrl, '音频资源', audio.prompt);
          setAudioSelectorOpen(false);
        }}
        onClose={() => setAudioSelectorOpen(false)}
      />
      <Transition appear show={imageEditorOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[80]" onClose={() => setImageEditorOpen(false)}>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Dialog.Panel className="w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl bg-bg-surface border border-border-subtle shadow-2xl">
                <ImagesTab
                  videoClip={videoClip}
                  refreshVideoClip={refreshVideoClip}
                  activeFrameType={imageEditorFrameType}
                  onFrameTypeChange={setImageEditorFrameType}
                  onRequestClose={() => setImageEditorOpen(false)}
                />
              </Dialog.Panel>
            </div>
          </div>
        </Dialog>
      </Transition>
      <Transition appear show={previewEditorTarget !== null} as={Fragment}>
        <Dialog as="div" className="relative z-[80]" onClose={() => setPreviewEditorTarget(null)}>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Dialog.Panel className="w-full max-w-6xl max-h-[90vh] overflow-hidden rounded-2xl bg-bg-surface border border-border-subtle shadow-2xl">
                {previewEditorTarget === 'audio' && (
                  <AudioTab
                    videoClip={videoClip}
                    refreshVideoClip={refreshVideoClip}
                    onRequestClose={() => setPreviewEditorTarget(null)}
                  />
                )}
                {previewEditorTarget === 'effects' && (
                  <EffectsTab
                    videoClip={videoClip}
                    refreshVideoClip={refreshVideoClip}
                    onRequestClose={() => setPreviewEditorTarget(null)}
                  />
                )}
                {previewEditorTarget === 'video' && (
                  <VideoTab
                    videoClip={videoClip}
                    refreshVideoClip={refreshVideoClip}
                    editConfig={editConfig}
                    onEditConfigChange={onEditConfigChange}
                    onRequestClose={() => setPreviewEditorTarget(null)}
                  />
                )}
              </Dialog.Panel>
            </div>
          </div>
        </Dialog>
      </Transition>
    </ReactFlow>
  );
}

export default function ClipCanvasFlow(props: ClipCanvasFlowProps) {
  return (
    <ReactFlowProvider>
      <ClipCanvasFlowInner {...props} />
    </ReactFlowProvider>
  );
}
